import React, { useState, useEffect, useRef, useCallback } from "react";
import Navbar from "../components/Navbar";

// ── 2FA Kalkan Laboratuvarı — İnteraktif 2FA Kurulum & Test Simülatörü ────────
// Konsept: Gerçek 2FA kurulum akışını simüle et.
// Benzersiz mekanik: Kullanıcı bir hesap için adım adım 2FA kurar (QR tarama → kod girişi → test)
// ve ardından bu hesabı gerçek zamanlı bir brute force saldırısına karşı savunur.

const STEP_LABELS = ["Hesabı Seç", "QR Tara", "Kodu Doğrula", "Saldırıyı Durdur", "Sonuç"];

const ACCOUNTS = [
  { id:"email",  name:"E-Posta Hesabı", icon:"📧", color:"#f97316", riskNote:"Diğer tüm hesaplar buradan sıfırlanabilir." },
  { id:"bank",   name:"Banka Hesabı",   icon:"🏦", color:"#ef4444", riskNote:"Finansal varlıkların doğrudan hedefte." },
  { id:"social", name:"Sosyal Medya",   icon:"📱", color:"#a855f7", riskNote:"Kimlik hırsızlığı ve itibar saldırıları riski." },
];

// Sahte TOTP kodu oluşturma (her 30 saniyede yeni kod)
const generateTOTP = (seed) => {
  const now = Math.floor(Date.now() / 30000);
  const combined = (now * 7919 + seed * 31337) % 1000000;
  return String(combined).padStart(6, "0");
};

export default function KalkanLab() {
  const [step, setStep]             = useState(0);  // 0-4
  const [selectedAcc, setSelected]  = useState(null);
  const [qrScanned, setQrScanned]   = useState(false);
  const [totp, setTotp]             = useState("");
  const [correctTotp, setCorrect]   = useState("");
  const [totpTimer, setTotpTimer]   = useState(30);
  const [totpError, setTotpError]   = useState(false);
  const [totpSuccess, setSuccess]   = useState(false);

  // Saldırı aşaması
  const [attackPhase, setAttackPhase] = useState("waiting"); // waiting|active|blocked|failed
  const [attackProgress, setProgress] = useState(0);
  const [attackLog, setAttackLog]     = useState([]);
  const [shieldHP, setShieldHP]       = useState(100);
  const [, setManual] = useState(false);
  const [notifCode, setNotif]         = useState("");      // telefona gelen bildirim kodu
  const [notifInput, setNotifInput]   = useState("");
  const [has2FA, setHas2FA]           = useState(false);

  const logRef = useRef(null);
  const attackRef = useRef(null);
  const seed = useRef(Math.floor(Math.random() * 9999));

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [attackLog]);

  // TOTP zamanlayıcı
  useEffect(() => {
    if (step !== 2) return;
    const code = generateTOTP(seed.current);
    setCorrect(code);
    setTotpTimer(30 - (Math.floor(Date.now() / 1000) % 30));

    const iv = setInterval(() => {
      const remaining = 30 - (Math.floor(Date.now() / 1000) % 30);
      setTotpTimer(remaining);
      if (remaining === 30) {
        const newCode = generateTOTP(seed.current);
        setCorrect(newCode);
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [step]);

  const addLog = useCallback((msg, color = "#94a3b8") => {
    setAttackLog(prev => [...prev, { msg, color, id: Date.now() + Math.random() }]);
  }, []);

  // Saldırı döngüsü
  useEffect(() => {
    if (attackPhase !== "active") return;
    let progress = 0;
    let hp = 100;

    addLog("🤖 Brute force motoru başlatılıyor...", "#fbbf24");
    addLog(`🎯 Hedef: ${selectedAcc?.name}`, "#94a3b8");

    const iv = setInterval(() => {
      progress += Math.random() * 8 + 3;
      hp -= Math.random() * 6 + 2;

      if (progress >= 30 && progress < 35) addLog("⚡ Sözlük saldırısı: 50.000 kombinasyon denendi...", "#94a3b8");
      if (progress >= 55 && progress < 62) addLog("🔥 Hibrit saldırı: Kişisel bilgiler eklenerek deneniyor...", "#fbbf24");
      if (progress >= 75 && progress < 82) addLog("💥 ŞİFRE BULUNDU! Giriş denemesi yapılıyor...", "#ef4444");

      setProgress(Math.min(progress, 100));
      setShieldHP(Math.max(hp, has2FA ? 15 : 0));

      if (progress >= 100) {
        clearInterval(iv);
        if (has2FA) {
          // 2FA var — kullanıcıdan onay gerekiyor
          const code = String(Math.floor(100000 + Math.random() * 900000));
          setNotif(code);
          setAttackPhase("awaiting_2fa");
          addLog("🛡️ 2FA KATMANI AKTİF — Telefona kod gönderildi!", "#60a5fa");
          addLog(`📱 SMS: Giriş denemesi tespit edildi. Kod: ${code}`, "#a78bfa");
        } else {
          setAttackPhase("failed");
          addLog("💀 HESAP ELE GEÇİRİLDİ! 2FA olmadan savunma çöktü.", "#ef4444");
        }
      }
    }, 400);
    attackRef.current = iv;
    return () => clearInterval(iv);
  }, [attackPhase, has2FA, selectedAcc, addLog]);

  const verify2FA = () => {
    if (notifInput.trim() === notifCode) {
      setAttackPhase("blocked");
      addLog("✅ 2FA doğrulandı! Saldırı engellendi.", "#22c55e");
      setStep(4);
    } else {
      addLog("❌ Yanlış kod! Tekrar dene.", "#ef4444");
    }
  };

  const verifyTotp = () => {
    if (totp.trim() === correctTotp) {
      setSuccess(true);
      setHas2FA(true);
      setTimeout(() => setStep(3), 1000);
    } else {
      setTotpError(true);
      setTimeout(() => setTotpError(false), 1500);
    }
  };

  const startAttack = () => {
    setAttackPhase("active");
    setProgress(0);
    setShieldHP(100);
  };

  const sifirla = () => {
    setStep(0); setSelected(null); setQrScanned(false); setTotp(""); setCorrect(""); setTotpTimer(30);
    setTotpError(false); setSuccess(false); setAttackPhase("waiting"); setProgress(0);
    setAttackLog([]); setShieldHP(100); setManual(false); setNotif(""); setNotifInput(""); setHas2FA(false);
  };

  const acc = selectedAcc;

  return (
    <div style={{ minHeight:"100vh",background:"linear-gradient(135deg,#050810 0%,#0a0f1e 60%,#04080e 100%)",fontFamily:"'Sora',sans-serif",color:"#fff" }}>
      <Navbar />
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes glow{0%,100%{opacity:0.7}50%{opacity:1}}
        @keyframes shield{0%,100%{box-shadow:0 0 20px rgba(96,165,250,0.4)}50%{box-shadow:0 0 50px rgba(96,165,250,0.9)}}
        @keyframes attack{0%,100%{transform:translateX(0)}25%{transform:translateX(-3px)}75%{transform:translateX(3px)}}
        @keyframes qrScan{from{top:5%}to{top:90%}}
        @keyframes codeFade{from{opacity:0;transform:scale(0.9)}to{opacity:1;transform:scale(1)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
      `}</style>

      <div style={{ maxWidth:"700px",margin:"0 auto",padding:"2rem" }}>

        {/* Başlık */}
        <div style={{ textAlign:"center",marginBottom:"2rem" }}>
          <div style={{ fontSize:"3rem",marginBottom:"0.4rem" }}>🔐</div>
          <h1 style={{ margin:0,fontSize:"2rem",fontWeight:"800",background:"linear-gradient(90deg,#60a5fa,#a78bfa)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" }}>
            2FA Kalkan Laboratuvarı
          </h1>
          <p style={{ color:"#475569",fontSize:"0.87rem",marginTop:"0.35rem" }}>Gerçek 2FA kurulumunu adım adım deneyimle</p>
        </div>

        {/* Adım göstergesi */}
        <div style={{ display:"flex",justifyContent:"center",gap:"0",marginBottom:"2.5rem" }}>
          {STEP_LABELS.map((label, i) => (
            <div key={i} style={{ display:"flex",alignItems:"center" }}>
              <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:"0.3rem" }}>
                <div style={{ width:"32px",height:"32px",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.8rem",fontWeight:"700",border:`2px solid ${i<=step?"#60a5fa":"#1e293b"}`,background:i<step?"#60a5fa":i===step?"rgba(96,165,250,0.15)":"transparent",color:i<step?"#fff":i===step?"#60a5fa":"#334155",transition:"all .4s" }}>
                  {i < step ? "✓" : i+1}
                </div>
                <span style={{ fontSize:"0.62rem",color:i<=step?"#60a5fa":"#334155",fontWeight:"600",whiteSpace:"nowrap" }}>{label}</span>
              </div>
              {i < STEP_LABELS.length - 1 && (
                <div style={{ width:"50px",height:"2px",background:i<step?"#60a5fa":"#1e293b",marginBottom:"18px",transition:"background .4s" }} />
              )}
            </div>
          ))}
        </div>

        {/* ADIM 0: Hesap seç */}
        {step === 0 && (
          <div style={{ animation:"fadeUp .4s ease" }}>
            <div style={{ textAlign:"center",marginBottom:"1.25rem" }}>
              <h2 style={{ margin:0,fontSize:"1.2rem",fontWeight:"700",color:"#e2e8f0" }}>Hangi hesabı koruyacaksın?</h2>
              <p style={{ color:"#475569",fontSize:"0.85rem",marginTop:"0.3rem" }}>2FA kurmak istediğin hesabı seç</p>
            </div>
            <div style={{ display:"flex",flexDirection:"column",gap:"0.75rem" }}>
              {ACCOUNTS.map(a => (
                <button key={a.id} onClick={() => { setSelected(a); setStep(1); }}
                  style={{ display:"flex",alignItems:"center",gap:"1rem",background:"rgba(255,255,255,0.04)",border:"2px solid rgba(255,255,255,0.1)",borderRadius:"16px",padding:"1.1rem 1.5rem",cursor:"pointer",transition:"all .25s",textAlign:"left",color:"#fff" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = a.color; e.currentTarget.style.background = `${a.color}11`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                  <span style={{ fontSize:"2rem" }}>{a.icon}</span>
                  <div>
                    <div style={{ fontWeight:"700",fontSize:"0.95rem" }}>{a.name}</div>
                    <div style={{ color:"#64748b",fontSize:"0.78rem",marginTop:"0.15rem" }}>⚠️ {a.riskNote}</div>
                  </div>
                  <span style={{ marginLeft:"auto",color:a.color,fontSize:"1.2rem" }}>→</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ADIM 1: QR Tarama */}
        {step === 1 && acc && (
          <div style={{ animation:"fadeUp .4s ease",textAlign:"center" }}>
            <h2 style={{ margin:"0 0 0.35rem",fontSize:"1.2rem",color:"#e2e8f0" }}>QR Kodunu Tara</h2>
            <p style={{ color:"#475569",fontSize:"0.83rem",marginBottom:"1.5rem" }}>Authenticator uygulamasını aç ve QR kodu tara</p>

            {/* Sahte QR kodu */}
            <div style={{ position:"relative",width:"180px",height:"180px",margin:"0 auto 1.5rem",background:"#fff",borderRadius:"16px",padding:"12px",cursor:"pointer",boxShadow:"0 0 30px rgba(96,165,250,0.2)" }}
              onClick={() => setQrScanned(true)}>
              <svg viewBox="0 0 100 100" width="100%" height="100%">
                {/* QR grid pattern */}
                {[0,1,2,3,4,5,6].map(row =>
                  [0,1,2,3,4,5,6].map(col => {
                    const val = ((row*7+col)*13 + seed.current) % 2;
                    return val ? <rect key={`${row}-${col}`} x={row*14+1} y={col*14+1} width="12" height="12" fill="#0a0a0f" rx="1" /> : null;
                  })
                )}
                {/* Köşe işaretleri */}
                {[[0,0],[78,0],[0,78]].map(([x,y],i) => (
                  <g key={i}>
                    <rect x={x+1} y={y+1} width="20" height="20" fill="#0a0a0f" rx="2" />
                    <rect x={x+3} y={y+3} width="16" height="16" fill="#fff" rx="1" />
                    <rect x={x+5} y={y+5} width="12" height="12" fill="#0a0a0f" rx="1" />
                  </g>
                ))}
              </svg>
              {/* Tarama çizgisi */}
              {!qrScanned && (
                <div style={{ position:"absolute",left:0,right:0,height:"2px",background:"rgba(96,165,250,0.8)",top:"50%",animation:"qrScan 2s linear infinite",boxShadow:"0 0 8px rgba(96,165,250,0.6)" }} />
              )}
              {qrScanned && (
                <div style={{ position:"absolute",inset:0,background:"rgba(34,197,94,0.85)",borderRadius:"12px",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"3rem" }}>✅</div>
              )}
            </div>

            <p style={{ color:"#475569",fontSize:"0.8rem",marginBottom:"1.5rem" }}>
              {qrScanned ? <span style={{ color:"#86efac",fontWeight:"600" }}>QR kodu başarıyla tarandı!</span> : "QR koduna tıklayarak simüle et"}
            </p>

            <div style={{ display:"flex",gap:"0.75rem",justifyContent:"center" }}>
              <button onClick={() => setStep(0)} style={{ background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:"12px",padding:"0.7rem 1.5rem",color:"#94a3b8",cursor:"pointer",fontFamily:"'Sora',sans-serif" }}>
                ← Geri
              </button>
              <button onClick={() => { if(qrScanned) setStep(2); }} disabled={!qrScanned}
                style={{ background:qrScanned?"linear-gradient(135deg,#60a5fa,#6366f1)":"rgba(255,255,255,0.06)",border:"none",borderRadius:"12px",padding:"0.7rem 2rem",color:qrScanned?"#fff":"#334155",fontWeight:"700",cursor:qrScanned?"pointer":"not-allowed",fontFamily:"'Sora',sans-serif",transition:"all .3s" }}>
                Devam Et →
              </button>
            </div>
          </div>
        )}

        {/* ADIM 2: Kod Doğrula */}
        {step === 2 && (
          <div style={{ animation:"fadeUp .4s ease",textAlign:"center" }}>
            <h2 style={{ margin:"0 0 0.35rem",fontSize:"1.2rem",color:"#e2e8f0" }}>Kodu Doğrula</h2>
            <p style={{ color:"#475569",fontSize:"0.83rem",marginBottom:"1.5rem" }}>Authenticator uygulamasındaki 6 haneli kodu gir</p>

            {/* Sahte authenticator ekranı */}
            <div style={{ background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:"18px",padding:"1.5rem",marginBottom:"1.5rem",maxWidth:"280px",margin:"0 auto 1.5rem" }}>
              <div style={{ fontSize:"0.75rem",color:"#475569",fontWeight:"700",marginBottom:"0.5rem",textTransform:"uppercase" }}>🔐 Authenticator</div>
              <div style={{ fontSize:"0.85rem",color:"#94a3b8",marginBottom:"0.75rem" }}>{acc?.name}</div>
              {/* Kod gösterimi */}
              <div key={correctTotp} style={{ fontSize:"2.2rem",fontWeight:"900",letterSpacing:"0.3em",color:"#60a5fa",fontFamily:"monospace",marginBottom:"0.75rem",animation:"codeFade 0.5s ease" }}>
                {correctTotp.slice(0,3)} {correctTotp.slice(3)}
              </div>
              {/* Süre çubuğu */}
              <div style={{ background:"rgba(255,255,255,0.08)",borderRadius:"999px",height:"6px",overflow:"hidden" }}>
                <div style={{ height:"100%",borderRadius:"999px",background:totpTimer>10?"#22c55e":"#ef4444",width:`${(totpTimer/30)*100}%`,transition:"width 1s linear,background .3s" }} />
              </div>
              <div style={{ color:"#475569",fontSize:"0.72rem",marginTop:"0.35rem" }}>{totpTimer}s sonra yenileniyor</div>
            </div>

            {/* Giriş alanı */}
            <div style={{ display:"flex",gap:"0.5rem",justifyContent:"center",marginBottom:"1rem" }}>
              <input
                value={totp}
                onChange={e => setTotp(e.target.value.replace(/\D/g,"").slice(0,6))}
                onKeyDown={e => e.key === "Enter" && verifyTotp()}
                placeholder="000 000"
                maxLength={7}
                style={{ background:"rgba(0,0,0,0.5)",border:`2px solid ${totpError?"#ef4444":totpSuccess?"#22c55e":"rgba(255,255,255,0.15)"}`,borderRadius:"12px",padding:"0.85rem 1.25rem",color:"#fff",fontSize:"1.3rem",fontFamily:"monospace",outline:"none",textAlign:"center",letterSpacing:"0.2em",width:"160px",transition:"border-color .3s" }}
              />
              <button onClick={verifyTotp}
                style={{ background:"linear-gradient(135deg,#60a5fa,#6366f1)",border:"none",borderRadius:"12px",padding:"0.85rem 1.25rem",color:"#fff",fontWeight:"700",cursor:"pointer",fontSize:"0.9rem",fontFamily:"'Sora',sans-serif" }}>
                Doğrula
              </button>
            </div>

            {totpError && <p style={{ color:"#f87171",fontSize:"0.85rem" }}>❌ Yanlış kod — uygulamadaki kodu kontrol et!</p>}
            {totpSuccess && <p style={{ color:"#86efac",fontSize:"0.85rem",fontWeight:"700" }}>✅ 2FA başarıyla kuruldu!</p>}
          </div>
        )}

        {/* ADIM 3: Saldırıyı Durdur */}
        {step === 3 && (
          <div style={{ animation:"fadeUp .4s ease" }}>
            <div style={{ textAlign:"center",marginBottom:"1.5rem" }}>
              <div style={{ display:"inline-flex",alignItems:"center",gap:"0.5rem",background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.3)",borderRadius:"999px",padding:"0.4rem 1rem",marginBottom:"0.75rem" }}>
                <span style={{ color:"#86efac",fontSize:"0.82rem",fontWeight:"600" }}>🛡️ 2FA AKTİF — {acc?.name}</span>
              </div>
              <h2 style={{ margin:0,fontSize:"1.2rem",color:"#e2e8f0" }}>Şimdi saldırı altındasın!</h2>
              <p style={{ color:"#475569",fontSize:"0.83rem",marginTop:"0.3rem" }}>Saldırgan şifreni buldu. 2FA kalkanın dayanacak mı?</p>
            </div>

            {/* Kalkan HP */}
            <div style={{ background:"rgba(255,255,255,0.04)",border:`2px solid ${shieldHP>40?"rgba(96,165,250,0.4)":"rgba(239,68,68,0.4)"}`,borderRadius:"18px",padding:"1.5rem",marginBottom:"1.25rem",textAlign:"center",
              animation:attackPhase==="active"?"attack 0.4s infinite":"none" }}>
              <div style={{ fontSize:"3rem",marginBottom:"0.5rem",animation:attackPhase==="active"?"glow 0.8s infinite":"none" }}>🛡️</div>
              <div style={{ fontSize:"0.8rem",color:"#475569",marginBottom:"0.5rem",textTransform:"uppercase",letterSpacing:"0.06em" }}>Kalkan Gücü</div>
              <div style={{ background:"rgba(255,255,255,0.06)",borderRadius:"999px",height:"20px",overflow:"hidden",marginBottom:"0.5rem",position:"relative" }}>
                <div style={{ height:"100%",borderRadius:"999px",background:shieldHP>60?"#60a5fa":shieldHP>30?"#fbbf24":"#ef4444",width:`${shieldHP}%`,transition:"width 0.4s ease,background 0.3s",boxShadow:`0 0 12px ${shieldHP>60?"rgba(96,165,250,0.6)":"rgba(239,68,68,0.6)"}` }} />
                <div style={{ position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:"0.75rem",fontWeight:"700" }}>{Math.round(shieldHP)}%</div>
              </div>
              {/* Saldırı ilerleme */}
              {attackPhase === "active" && (
                <div style={{ marginTop:"0.75rem" }}>
                  <div style={{ color:"#ef4444",fontSize:"0.78rem",marginBottom:"0.35rem",fontFamily:"monospace",animation:"pulse 0.5s infinite" }}>⚡ SALDIRI İLERLEMESİ: {Math.round(attackProgress)}%</div>
                  <div style={{ background:"rgba(239,68,68,0.1)",borderRadius:"999px",height:"8px",overflow:"hidden" }}>
                    <div style={{ height:"100%",borderRadius:"999px",background:"#ef4444",width:`${attackProgress}%`,transition:"width 0.4s" }} />
                  </div>
                </div>
              )}
            </div>

            {/* 2FA onay talebi */}
            {attackPhase === "awaiting_2fa" && (
              <div style={{ background:"rgba(96,165,250,0.1)",border:"2px solid rgba(96,165,250,0.4)",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.25rem",animation:"shield 1s infinite" }}>
                <div style={{ color:"#60a5fa",fontWeight:"700",marginBottom:"0.5rem",display:"flex",alignItems:"center",gap:"0.5rem" }}>
                  <span>📱</span> Telefona Kod Geldi!
                </div>
                <p style={{ color:"#94a3b8",fontSize:"0.85rem",marginBottom:"1rem",lineHeight:"1.6" }}>
                  Saldırgan şifreni buldu. SMS kodunu girerek sen olduğunu doğrula.
                  <br/><strong style={{ color:"#fde68a" }}>Gerçek hayatta bu kodu <em>kimseyle paylaşma!</em></strong>
                </p>
                <div style={{ display:"flex",gap:"0.5rem" }}>
                  <input
                    value={notifInput}
                    onChange={e => setNotifInput(e.target.value.replace(/\D/g,"").slice(0,6))}
                    onKeyDown={e => e.key === "Enter" && verify2FA()}
                    placeholder="6 haneli kodu gir"
                    style={{ flex:1,background:"rgba(0,0,0,0.4)",border:"2px solid rgba(96,165,250,0.3)",borderRadius:"10px",padding:"0.7rem 1rem",color:"#fff",fontSize:"1.1rem",fontFamily:"monospace",outline:"none",textAlign:"center",letterSpacing:"0.15em" }}
                  />
                  <button onClick={verify2FA}
                    style={{ background:"linear-gradient(135deg,#60a5fa,#6366f1)",border:"none",borderRadius:"10px",padding:"0.7rem 1.25rem",color:"#fff",fontWeight:"700",cursor:"pointer",fontFamily:"'Sora',sans-serif" }}>
                    Onayla
                  </button>
                </div>
                <div style={{ color:"#475569",fontSize:"0.72rem",marginTop:"0.5rem",textAlign:"center" }}>
                  (Test için bildirim kodu: <span style={{ color:"#a78bfa",fontFamily:"monospace" }}>{notifCode}</span>)
                </div>
              </div>
            )}

            {attackPhase === "failed" && (
              <div style={{ background:"rgba(239,68,68,0.12)",border:"2px solid #ef4444",borderRadius:"14px",padding:"1.25rem",marginBottom:"1.25rem",textAlign:"center" }}>
                <div style={{ fontSize:"2rem",marginBottom:"0.5rem" }}>💀</div>
                <div style={{ color:"#f87171",fontWeight:"700",marginBottom:"0.5rem" }}>Hesap Ele Geçirildi!</div>
                <p style={{ color:"#94a3b8",fontSize:"0.85rem" }}>2FA olmadan şifre tek başına yeterli değildi.</p>
              </div>
            )}

            {/* Log */}
            {attackLog.length > 0 && (
              <div ref={logRef} style={{ background:"#000",border:"1px solid #0f1929",borderRadius:"12px",padding:"0.85rem",fontFamily:"monospace",fontSize:"0.78rem",maxHeight:"150px",overflowY:"auto",marginBottom:"1.25rem" }}>
                <div style={{ color:"#a78bfa",fontWeight:"700",marginBottom:"0.5rem",fontSize:"0.72rem" }}>▶ SALDIRI KONSOLU</div>
                {attackLog.map(l => <div key={l.id} style={{ color:l.color,marginBottom:"2px" }}>{l.msg}</div>)}
              </div>
            )}

            {attackPhase === "waiting" && (
              <div style={{ textAlign:"center" }}>
                <button onClick={startAttack}
                  style={{ background:"linear-gradient(135deg,#7c3aed,#ef4444)",border:"none",borderRadius:"14px",padding:"0.9rem 3rem",fontSize:"1rem",fontWeight:"800",color:"#fff",cursor:"pointer",boxShadow:"0 8px 25px rgba(124,58,237,0.4)" }}>
                  🚨 Saldırıyı Başlat
                </button>
              </div>
            )}
          </div>
        )}

        {/* ADIM 4: Sonuç */}
        {step === 4 && (
          <div style={{ animation:"fadeUp .6s ease",textAlign:"center" }}>
            <div style={{ fontSize:"4rem",marginBottom:"0.75rem" }}>🏆</div>
            <h2 style={{ margin:"0 0 0.5rem",fontSize:"1.5rem",color:"#e2e8f0" }}>2FA Kalkanı Dayanıklı!</h2>
            <p style={{ color:"#94a3b8",lineHeight:"1.7",marginBottom:"2rem",maxWidth:"460px",margin:"0 auto 2rem",fontSize:"0.9rem" }}>
              Saldırgan {acc?.name} şifreni bulmasına rağmen 2FA engeline takıldı. Telefona gelen kodu sadece sen görebildiğin için giriş imkânsız hale geldi.
            </p>

            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:"1rem",marginBottom:"2rem",maxWidth:"400px",margin:"0 auto 2rem" }}>
              {[
                { label:"Korunan Hesap", val:acc?.name, color:"#86efac", icon:"✅" },
                { label:"Engellenen Saldırı", val:"Brute Force", color:"#60a5fa", icon:"🛡️" },
                { label:"2FA Yöntemi", val:"TOTP + SMS", color:"#a78bfa", icon:"📱" },
                { label:"Güvenlik Seviyesi", val:"Maksimum", color:"#fbbf24", icon:"🔐" },
              ].map(s => (
                <div key={s.label} style={{ background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"14px",padding:"1rem" }}>
                  <div style={{ fontSize:"1.4rem",marginBottom:"0.3rem" }}>{s.icon}</div>
                  <div style={{ color:s.color,fontWeight:"700",fontSize:"0.88rem" }}>{s.val}</div>
                  <div style={{ color:"#475569",fontSize:"0.72rem",marginTop:"0.2rem" }}>{s.label}</div>
                </div>
              ))}
            </div>

            <div style={{ background:"rgba(96,165,250,0.07)",border:"1px solid rgba(96,165,250,0.2)",borderRadius:"14px",padding:"1.1rem",marginBottom:"1.5rem",textAlign:"left",maxWidth:"480px",margin:"0 auto 1.5rem" }}>
              <div style={{ color:"#60a5fa",fontWeight:"700",fontSize:"0.82rem",marginBottom:"0.5rem" }}>📚 Öğrendiklerin</div>
              <ul style={{ color:"#94a3b8",fontSize:"0.82rem",lineHeight:"1.8",margin:0,paddingLeft:"1.25rem" }}>
                <li>TOTP kodları her 30 saniyede yenilenir — çalınsalar bile kısa sürede geçersiz olur</li>
                <li>SMS 2FA, TOTP'den daha az güvenlidir ama hiç yoktan çok iyidir</li>
                <li>2FA kodunu kimseyle, hiçbir zaman paylaşma — asıl kurum istemez</li>
              </ul>
            </div>

            <button onClick={sifirla} style={{ background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:"12px",padding:"0.75rem 2rem",color:"#fff",fontSize:"0.95rem",fontWeight:"600",cursor:"pointer" }}>
              🔄 Baştan Dene
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
